// The working week as people read it (4.3d.4): day names in the screen's language, in the order that language's week runs.
//
// THE STORED KEYS STAY AS THEY ARE. Working hours are saved under 'Mon','Tus','Wed','Thur','Fri','Sat','Sun' — the engine and
// the agent read those exact keys — so the keys are data, never words. Showing them raw is how the settings card came to read
// "Tus 09:00" and "Thur 09:00" in whatever order they were saved, and slicing the Arabic name to two letters is how every day
// pill came to read "ال". Pure: scripts/check-times.cjs calls these.
export const DAY_KEYS = Object.freeze(["Mon", "Tus", "Wed", "Thur", "Fri", "Sat", "Sun"]);
// JavaScript's weekday number (Sunday = 0) of each stored key.
const INDEX = Object.freeze({ Sun: 0, Mon: 1, Tus: 2, Wed: 3, Thur: 4, Fri: 5, Sat: 6 });
// 2024-01-07 was a Sunday: + n days is weekday n, at noon UTC so no zone moves it to another day.
const dateOf = (key) => new Date(Date.UTC(2024, 0, 7 + INDEX[key], 12));

export function dayName(key, lang, style = "short") {
  if (!(key in INDEX)) return String(key ?? "");
  try { return new Intl.DateTimeFormat(lang || undefined, { weekday: style, timeZone: "UTC" }).format(dateOf(key)); }
  catch { return key; }
}

// The day a week starts on in this language (1 = Monday … 7 = Sunday), as the browser knows it; Monday when it does not.
export function weekStart(lang) {
  try {
    const loc = new Intl.Locale(lang || "en");
    const info = typeof loc.getWeekInfo === "function" ? loc.getWeekInfo() : loc.weekInfo;
    const first = Number(info?.firstDay);
    return first >= 1 && first <= 7 ? first : 1;
  } catch { return 1; }
}

// The stored keys in this language's week order.
export function orderedDays(lang) {
  const first = weekStart(lang) % 7;   // 7 (Sunday) → 0
  return [...DAY_KEYS].sort((a, b) => ((INDEX[a] - first + 7) % 7) - ((INDEX[b] - first + 7) % 7));
}

// [key, value] pairs of a saved week, in this language's week order; unknown keys last, as they were.
export function sortDayEntries(entries, lang) {
  const order = orderedDays(lang);
  const rank = (k) => { const i = order.indexOf(k); return i < 0 ? 99 : i; };
  return [...(entries ?? [])].sort((a, b) => rank(a[0]) - rank(b[0]));
}

// "09:00–02:00", the dash a real dash; overnight when it closes the next day.
export const isOvernight = (from, to) => Boolean(from && to && String(to) < String(from));
